import { Dispatch, useReducer } from 'react';
import { capitalize, get } from 'lodash';
import {
  compact,
  filter,
  isEmpty,
  map,
  mapValues,
  negate,
  pick,
  pipe,
  set,
} from 'lodash/fp';
import {
  FieldName,
  FieldState,
  FieldValue,
  HostedFieldAdditionalData,
  BankAccountType,
  BankOwnershipType,
  State,
  Validator,
  Validators,
  NormalFieldState,
  SensitiveFieldState,
  CreditCardFormFields,
  BankFormFields,
  Fields,
  FieldError,
  PaymentSourceType,
} from '@zapier/payment/src/types';

const parseName = (name: string) => {
  try {
    const [firstName, lastName] = name.split(' ');
    return [firstName, lastName];
  } catch (_) {
    return '';
  }
};

/**
 * Create a field state.
 */
const createFieldState = (
  prettyName: string,
  value?: string | null | undefined
) => ({
  error: null,
  isSensitive: false,
  prettyName,
  value: value ?? '',
});

/**
 * Create a sensitive field state.
 */
const createSensitiveFieldState = (prettyName: string) => ({
  error: null,
  isSensitive: true,
  prettyName,
  value: {
    isEmpty: true,
    originalError: '',
  },
});

const createBankFields = (savedAccount?: {} | null): BankFormFields => ({
  accountNumber: createFieldState('account number'),
  routingNumber: createFieldState(
    'bank routing number',
    get(savedAccount, 'routingNumber')
  ),
  accountType: createFieldState(
    'bank account type',
    get(savedAccount, 'accountType', BankAccountType.checking)
  ),
  ownershipType: createFieldState(
    'bank account ownership type',
    get(savedAccount, 'ownershipType', BankOwnershipType.business)
  ),
  firstName: createFieldState(
    'first name',
    parseName(get(savedAccount, 'accountHolderName'))[0]
  ),
  lastName: createFieldState(
    'last name',
    parseName(get(savedAccount, 'accountHolderName'))[1]
  ),
  businessName: createFieldState('business name'),
  streetAddress: createFieldState('street address'),
  extendedAddress: createFieldState('extended address'),
  locality: createFieldState('city', get(savedAccount, 'addressCity')),
  region: createFieldState('state', get(savedAccount, 'addressState')),
  postalCode: createFieldState('zip', get(savedAccount, 'addressZip')),
  isSubmethodSupported: createFieldState('banking type support', 'supported'),
  currency: createFieldState('selected currency'),
});

const createCardFields = (savedCard?: {} | null): CreditCardFormFields => ({
  // The order of these decides the order that form errors will show
  // up so it's important to keep them in the same order as the form
  // fiels so the error message is more easily digestable for the user
  name: createFieldState('name', get(savedCard, 'name')),
  number: createSensitiveFieldState('card number'),
  cvc: createSensitiveFieldState('card CVC'),
  expiry: createSensitiveFieldState('card expiration date'),
  addressCountry: createFieldState('country', get(savedCard, 'addressCountry')),
  addressState: createFieldState('state', get(savedCard, 'addressState')),
  addressZip: createFieldState('zip', get(savedCard, 'addressZip')),
  /**
   * This is silly to have to use a string here, but slotting in card type support into these fields require strings.
   * This ideally would be a boolean value, but unless we refactored how this form state handler works, we have to settle
   * for strings.
   */
  isSubmethodSupported: createFieldState('card type support', 'supported'),
  currency: createFieldState('selected currency'),
});

function assertUnreachable(method: PaymentSourceType): never {
  throw new Error(`Payment method ${method} doesn't support form state`);
}

/**
 * Creates field states
 */
const createFields = (
  method: PaymentSourceType,
  savedDetails?: object | null
) => {
  switch (method) {
    case PaymentSourceType.bank:
      return createBankFields(savedDetails);

    case PaymentSourceType.card:
      return createCardFields(savedDetails);

    default:
      return assertUnreachable(method);
  }
};

const createState = (fields: Fields, validators: any) => ({
  fields,
  hasErrors: false,
  hasSubmitted: false,
  isDirty: false,
  isTokenizing: false,
  isValid: false,
  validators,
});

/** Validators */
/**
 * Validate that a field is not empty.
 */
const validateNotEmpty: Validator<NormalFieldState> = fieldState =>
  fieldState.value === ''
    ? `Your ${fieldState.prettyName} must be specified.`
    : '';

/**
 * Validate that a sensitive field is not empty, complete and has no errors.
 */
const validateSensitiveField: Validator<SensitiveFieldState> = fieldState =>
  fieldState.value.isEmpty
    ? `Your ${fieldState.prettyName} must be specified.`
    : fieldState.value.originalError;

const validateSubmethodSupported: Validator<NormalFieldState> = (
  fieldState,
  fields
) =>
  fieldState.value === 'unsupported'
    ? `That payment method doesn't support ${fields.currency.value}. Please try a different payment method.`
    : '';

/**
 * Map of normal fields validators.
 */
const cardValidators: Validators<CreditCardFormFields> = {
  addressCountry: validateNotEmpty,
  addressState: (fieldState, fields) =>
    fields.addressCountry.value === 'US'
      ? validateNotEmpty(fieldState, fields)
      : '',
  addressZip: (fieldState, fields) =>
    fields.addressCountry.value === 'US' // $FlowFixMe: Flow is quite rigid here and assume that we may add the `length` properly to `HostedFieldValue`...
      ? fieldState.value &&
        fieldState.value.length > 0 &&
        fieldState.value.length < 5
        ? 'Zip code is incomplete.'
        : validateNotEmpty(fieldState, fields)
      : '',
  name: validateNotEmpty,
  cvc: validateSensitiveField,
  expiry: validateSensitiveField,
  number: validateSensitiveField,
  isSubmethodSupported: validateSubmethodSupported,
  currency: () => '',
};

const bankValidators: Validators<BankFormFields> = {
  accountNumber: validateNotEmpty,
  routingNumber: validateNotEmpty,
  accountType: fieldState =>
    !(fieldState.value in BankAccountType)
      ? `${capitalize(fieldState.value)} is not a valid account type`
      : '',
  ownershipType: fieldState =>
    !(fieldState.value in BankOwnershipType)
      ? `${capitalize(fieldState.value)} is not a valid ownership type`
      : '',
  firstName: (fieldState, fields) =>
    fields.ownershipType.value === BankOwnershipType.personal
      ? validateNotEmpty(fieldState, fields)
      : '',
  lastName: (fieldState, fields) =>
    fields.ownershipType.value === BankOwnershipType.personal
      ? validateNotEmpty(fieldState, fields)
      : '',
  businessName: (fieldState, fields) =>
    fields.ownershipType.value === BankOwnershipType.business
      ? validateNotEmpty(fieldState, fields)
      : '',
  streetAddress: validateNotEmpty,
  extendedAddress: () => '',
  locality: validateNotEmpty,
  region: validateNotEmpty,
  postalCode: validateNotEmpty,
  isSubmethodSupported: validateSubmethodSupported,
  currency: () => '',
};

const createValidators = (
  method: PaymentSourceType
): Validators<BankFormFields | CreditCardFormFields> | {} => {
  switch (method) {
    case PaymentSourceType.bank:
      return bankValidators;

    case PaymentSourceType.card:
      return cardValidators;

    default:
      return {};
  }
};

/**
 * Validate a field given its name and value.
 */
const validateField = (name: FieldName, fieldState: FieldState, state: State) =>
  // @ts-ignore - This needs to be addressed...cannot index `{}` with `string`
  state.validators[name](fieldState, state.fields);

/**
 * Update a field value given its name.
 */
const updateFieldValue = (name: FieldName, value: FieldValue) => (
  state: State
) => set(['fields', name, 'value'], value ?? '', state);

/**
 * Build an action to update a field error with the given validator.
 */
const updateFieldError = (name: FieldName) => (state: State) => {
  // @ts-ignore - This needs to be addressed, fields can't be indexed by `string`
  const fieldState = state.fields[name];
  const errorMessage = validateField(name, fieldState, state);
  const currentError = fieldState.error;
  const error = errorMessage
    ? {
        message: errorMessage,
        isFixed: false,
      }
    : currentError
    ? {
        message: currentError.message,
        isFixed: true,
      }
    : null;
  return set(['fields', name, 'error'], error, state);
};

/**
 * Reset the state and zip field values if the user changed the country from US to another one.
 */
const maybeResetStateAndZip = (name: FieldName, value: FieldValue) => (
  state: State
) => {
  if (
    name === 'addressCountry' &&
    value !== 'US' &&
    'addressCountry' in state.fields &&
    state.fields.addressCountry &&
    state.fields.addressCountry.value === 'US'
  ) {
    return pipe([
      set(['fields', 'addressState', 'value'], ''),
      set(['fields', 'addressZip', 'value'], ''),
    ])(state);
  }

  return state;
};

/**
 * Update the form error flag. Turn it on if at least one field has an error.
 */
const maybeUpdateErrorFlag = (state: State) =>
  set(
    ['hasErrors'],
    state.hasSubmitted
      ? pipe([map('error'), compact, negate(isEmpty)])(state.fields)
      : false,
    state
  );

/**
 * Update the form valid flag. Turn it on if at least one field has an unresolved error.
 */
const maybeUpdateValidFlag = (state: State) => {
  return set(
    ['isValid'],
    state.hasSubmitted
      ? pipe([
          map('error'),
          filter({
            isFixed: false,
          }),
          isEmpty,
        ])(state.fields)
      : false,
    state
  );
};

type FormStateAction =
  | { type: 'updateField'; name: FieldName; value: FieldValue }
  | { type: 'startTokenization' }
  | { type: 'stopTokenization' }
  | { type: 'clearErrors' }
  | { type: 'updateErrors' };

/**
 * Update a field state given its name and value.
 */
export const updateField = (name: FieldName, value: FieldValue, state: State) =>
  pipe([
    set(['isDirty'], true),
    updateFieldValue(name, value),
    updateFieldError(name),
    maybeResetStateAndZip(name, value),
    maybeUpdateErrorFlag,
    maybeUpdateValidFlag,
  ])(state);

/**
 * Update all fields error at once.
 */
const updateFieldsError = (state: State) => {
  const updateFieldsFns = Object.keys(state.fields).map(key =>
    updateFieldError(key)
  );
  return pipe([...updateFieldsFns, maybeUpdateErrorFlag, maybeUpdateValidFlag])(
    state
  );
};

/**
 * Check if the form has any errors.
 */
const updateErrors = (state: State) =>
  pipe([set(['hasSubmitted'], true), updateFieldsError])(state);

const checkIsValid = (state: State) => () => {
  const { isValid } = updateErrors(state);
  return isValid;
};

const getAdditionalDataSelector = (
  state: State
): (() => HostedFieldAdditionalData) => () =>
  pipe([
    pick(['name', 'addressState', 'addressZip', 'addressCountry']),
    mapValues('value'),
  ])(state.fields);

const getFieldsErrorsSelector = (state: State) => () =>
  // I don't love typecasting here, but it's a struggle getting TS to interpret types here
  (map('error', state.fields) as FieldError[]).filter(Boolean);

const clearErrors = (state: State) => ({
  ...state,
  // @ts-ignore - state.fields doesn't fit in here
  fields: Object.entries<Fields>(state.fields).reduce(
    (acc, [key, field]) => ({ ...acc, [key]: { ...field, error: null } }),
    {}
  ) as Fields,
  hasErrors: false,
  isDirty: false,
});

type FormStateActionFn = (
  state: State,
  dispatch: Dispatch<FormStateAction>
) => (...args: any[]) => void;

const startTokenizationAction: FormStateActionFn = (_state, dispatch) => () => {
  dispatch({
    type: 'startTokenization',
  });
};

const stopTokenizationAction: FormStateActionFn = (_state, dispatch) => () => {
  dispatch({
    type: 'stopTokenization',
  });
};

const clearErrorsAction: FormStateActionFn = (_state, dispatch) => () => {
  dispatch({
    type: 'clearErrors',
  });
};

const updateErrorsAction: FormStateActionFn = (_state, dispatch) => () => {
  dispatch({
    type: 'updateErrors',
  });
};

const updateFieldAction: FormStateActionFn = (_state, dispatch) => (
  name: FieldName,
  value: FieldValue
) => {
  dispatch({
    type: 'updateField',
    name,
    value,
  });
};

export const reducer = (state: State, action: FormStateAction): State => {
  switch (action.type) {
    case 'updateField':
      return updateField(action.name, action.value, state);

    case 'startTokenization':
      return set(['isTokenizing'], true, state);

    case 'stopTokenization':
      return set(['isTokenizing'], false, state);

    case 'clearErrors':
      return clearErrors(state);

    case 'updateErrors':
      return updateErrors(state);

    default:
      return state;
  }
};

const useFormState = (
  method: PaymentSourceType,
  savedDetails?: object | null
) => {
  const initialFieldsState = createFields(method, savedDetails);
  const validators = createValidators(method);
  const initialState = createState(initialFieldsState, validators);
  const [state, dispatch] = useReducer(reducer, initialState);
  return {
    state,
    updateErrors: updateErrorsAction(state, dispatch),
    clearErrors: clearErrorsAction(state, dispatch),
    getAdditionalData: getAdditionalDataSelector(state),
    getFieldsErrors: getFieldsErrorsSelector(state),
    startTokenization: startTokenizationAction(state, dispatch),
    stopTokenization: stopTokenizationAction(state, dispatch),
    updateField: updateFieldAction(state, dispatch),
    checkIsValid: checkIsValid(state),
  };
};

export default useFormState;
